import React from 'react';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { IndexLink, Link, withRouter } from 'react-router';
import { FormattedMessage } from 'react-intl';
import Menu from '../Menu';
import { active } from './styles.css';
import HasRole from '../HasRole';
import SubscriptionActive from '../SubscriptionActive';
import SubscriptionInactive from '../SubscriptionInactive';
import FeatureEnabled from '../FeatureEnabled';
import FeatureDisabled from '../FeatureDisabled';
import { withEnabledFeatures } from '../../utils/features';
import { withCommunityProfileIdQuery } from '../../utils/community';
import * as constants from '../../containers/Authorization/constants';

function getCommunityActiveClass(router) {
  if (router.location.pathname.includes('/counselor-community/')) {
    return active;
  }
  return '';
}

function getCommunityServerlessActiveClass(router) {
  /* istanbul ignore next */
  if (router.location.pathname.includes('/counselorcommunity/')) {
    return active;
  }
  return '';
}
function getCollegeProfileActiveClass(router) {
  return router.location.pathname.includes('/naviance-college-profile/') ? active : '';
}
function getRepVisitsActiveClass(router) {
  return router.location.pathname.includes('/rep-visits/') ? active : '';
}

function getCollegeEventsActiveClass(router) {
  return router.location.pathname.includes('/college-events/') ? active : '';
}

function getAdvancedAwarenessClass(router) {
  return router.location.pathname.includes('/advanced-awareness') ? active : '';
}

function getConnectionClass(router) {
  return router.location.pathname.includes('/connection') ? active : '';
}

function getAMPlusActiveClass(router) {
  /* istanbul ignore next */
  return router.location.pathname.includes('/am-plus/') ? active : '';
}
function getCollegeScholarshipsClass(router) {
  /* istanbul ignore next */
  return router.location.pathname.includes('/college-scholarships/') ? active : '';
}

function shouldMenuLinkBeFocused(router) {
  return router.location.pathname === '/';
}

function shouldGraphiqlLinkBeFocused(router) {
  return router.location.pathname.includes('/graphiql');
}

const ActiveMatchConnectionsMenu = ({ idPrefix, router, onMenuSelect }) => (
  <div>
    <Menu.Header tabIndex="0" as="div" styleName="menu-sub-header-nav">
      <FormattedMessage tagName="h5" id="app.mainMenu.subtitle.connections" defaultMessage="Connection" />
    </Menu.Header>
    <Menu
      as="dl"
      aria-label="Navigation Menu"
      styleName="menu-nav"
      vertical
      inverted
      size="huge"
      onClick={onMenuSelect}
      role="navigation"
    >
      <Menu.Item as="dt" link styleName="menu-item-nav">
        <Link
          to="/am-plus/"
          id={`js-${idPrefix}-am-plus-menu-link`}
          className={getAMPlusActiveClass(router)}
          styleName="menu-link-nav-update"
          activeClassName={active}
        >
          <FormattedMessage tagName="h3" id="app.mainMenu.link.amPlus" defaultMessage="ActiveMatch" />
        </Link>
      </Menu.Item>
    </Menu>
  </div>
);

const canAccessLegacyCommunity = ({ enabledFeatures, communityProfile }) => {
  const isServerlessCommunityOn = (enabledFeatures || []).includes(constants.COMMUNITY_SERVERLESS);
  const communityProfileId = get(communityProfile, 'viewer.profile.communityId', '');
  const communityIdMatchesUUID = /\b[0-9a-f]{8}\b-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-\b[0-9a-f]{12}\b/.test(communityProfileId);
  const isCommunityProfileServerless = !communityProfileId || communityIdMatchesUUID;
  return !isServerlessCommunityOn || !isCommunityProfileServerless;
};

const MainMenu = ({ idPrefix, router, onMenuSelect, enabledFeatures, communityProfile }) => (
  <div>
    <Menu
      as="dl"
      aria-label="Home Menu"
      styleName="menu-nav"
      vertical
      inverted
      size="huge"
      onClick={onMenuSelect}
      role="navigation"
    >
      <Menu.Item as="dt" link styleName="menu-item-nav">
        <IndexLink
          aria-label="Home"
          aria-current={shouldMenuLinkBeFocused(router) ? 'true' : 'false'}
          to="/"
          id={`js-${idPrefix}-home-menu-link`}
          styleName="menu-link-nav-update"
          activeClassName={active}
        >
          <FormattedMessage tagName="h3" id="app.mainMenu.link.home" defaultMessage="Home" />
        </IndexLink>
      </Menu.Item>
    </Menu>
    <div>
      <Menu.Header tabIndex="0" as="div" styleName="menu-sub-header-nav">
        <FormattedMessage tagName="h5" id="app.mainMenu.subtitle.awareness" defaultMessage="Awareness" />
      </Menu.Header>
      <Menu
        as="dl"
        aria-label="Awareness Menu"
        styleName="menu-nav"
        vertical
        inverted
        size="huge"
        onClick={onMenuSelect}
        role="navigation"
      >
        <FeatureEnabled anyOf={constants.COMMUNITY_SERVERLESS}>
          <Menu.Item as="dt" link styleName="menu-item-nav">
            <Link
              aria-label="Counselor Community"
              aria-current={/* istanbul ignore next */getCommunityServerlessActiveClass(router) ? 'true' : 'false'}
              to="/counselorcommunity/"
              id={`js-${idPrefix}-counselor-community-serverless-menu-link`}
              className={getCommunityServerlessActiveClass(router)}
              styleName="menu-link-nav-update"
              activeClassName={active}
            >
              <FormattedMessage tagName="h3" id="app.mainMenu.link.counselorcommunity" defaultMessage="Counselor Community" />
            </Link>
          </Menu.Item>
        </FeatureEnabled>
        {canAccessLegacyCommunity({ enabledFeatures, communityProfile }) && (
          <FeatureDisabled anyOf={constants.DISABLE_LEGACY_COMMUNITY}>
            <Menu.Item as="dt" link styleName="menu-item-nav">
              <Link
                aria-label="Counselor Community"
                aria-current={getCommunityActiveClass(router) ? 'true' : 'false'}
                to="/counselor-community/"
                id={`js-${idPrefix}-counselor-community-menu-link`}
                className={getCommunityActiveClass(router)}
                styleName="menu-link-nav-update"
                activeClassName={active}
              >
                <FormattedMessage tagName="h3" id="app.mainMenu.link.counselorcommunity" defaultMessage="Counselor Community" />
              </Link>
            </Menu.Item>
          </FeatureDisabled>
        )}
        <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
          <FeatureEnabled anyOf={constants.CAMINO_HEM_TRANSFER_INFO}>
            <Menu.Item as="dt" link styleName="menu-item-nav">
              <Link
                aria-label="College Profile"
                aria-current={getCollegeProfileActiveClass(router) ? 'true' : 'false'}
                to="/naviance-college-profile/edit"
                id={`js-${idPrefix}-college-profile-menu-link`}
                className={getCollegeProfileActiveClass(router)}
                styleName="menu-link-nav-update"
                activeClassName={active}
              >
                <FormattedMessage tagName="h3" id="app.mainMenu.link.collegeProfile" defaultMessage="College Profile" />
              </Link>
            </Menu.Item>
          </FeatureEnabled>
          <FeatureDisabled anyOf={constants.CAMINO_HEM_TRANSFER_INFO}>
            <Menu.Item as="dt" link styleName="menu-item-nav">
              <Link
                aria-label="Naviance College Profile"
                aria-current={getCollegeProfileActiveClass(router) ? 'true' : 'false'}
                to="/naviance-college-profile/edit"
                id={`js-${idPrefix}-naviance-college-profile-menu-link`}
                className={getCollegeProfileActiveClass(router)}
                styleName="menu-link-nav-update"
                activeClassName={active}
              >
                <FormattedMessage tagName="h3" id="app.mainMenu.link.naviancecollegeProfile" defaultMessage="Naviance College Profile" />
              </Link>
            </Menu.Item>
          </FeatureDisabled>
        </HasRole>
        <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
          <SubscriptionActive anyOf={constants.ADV_AWARENESS_SUBSCRIPTION}>
            <Menu.Item as="dt" link styleName="menu-item-nav">
              <Link
                aria-label="Advanced Awareness"
                aria-current={getAdvancedAwarenessClass(router) ? 'true' : 'false'}
                to="/advanced-awareness"
                id={`js-${idPrefix}-advanced-awareness-menu-link`}
                className={getAdvancedAwarenessClass(router)}
                styleName="menu-link-nav-update"
                activeClassName={active}
              >
                <FormattedMessage tagName="h3" id="app.mainMenu.link.advancedAwareness" defaultMessage="Advanced Awareness" />
              </Link>
            </Menu.Item>
          </SubscriptionActive>
        </HasRole>
        <FeatureEnabled allOf={constants.SCHOLARSHIPS_SUBSCRIPTION}>
          <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
            <SubscriptionActive anyOf={constants.SCHOLARSHIPS_SUBSCRIPTION}>
              <Menu.Item as="dt" link styleName="menu-item-nav">
                <Link
                  aria-label="College Scholarships"
                  aria-current={/* istanbul ignore next */getCollegeScholarshipsClass(router) ? 'true' : 'false'}
                  to="/college-scholarships"
                  id={`js-${idPrefix}-college-scholarships-menu-link`}
                  className={getCollegeScholarshipsClass(router)}
                  styleName="menu-link-nav-update"
                  activeClassName={active}
                >
                  <FormattedMessage tagName="h3" id="app.mainMenu.link.collegeScholarships" defaultMessage="Scholarships" />
                </Link>
              </Menu.Item>
            </SubscriptionActive>
          </HasRole>
        </FeatureEnabled>
      </Menu>
    </div>
    {process.env.GRAPHIQL && (
    <Menu
      as="dl"
      aria-label="Graphiql Menu"
      styleName="menu-nav"
      vertical
      inverted
      size="huge"
      onClick={onMenuSelect}
      role="navigation"
    >
      <Menu.Item as="dt" link styleName="menu-item-nav">
        <Link
          aria-label="Graphiql"
          aria-current={/* istanbul ignore next */ shouldGraphiqlLinkBeFocused(router) ? 'true' : 'false'}
          to="/graphiql"
          id={`js-${idPrefix}-graphiql-menu-link`}
          styleName="menu-link-nav-update"
          activeClassName={active}
        >
          <FormattedMessage tagName="h3" id="app.mainMenu.link.graphiql" defaultMessage="GraphiQL" />
        </Link>
      </Menu.Item>
    </Menu>
    )}
    <Menu.Header tabIndex="0" as="div" styleName="menu-sub-header-nav">
      <FormattedMessage tagName="h5" id="app.mainMenu.subtitle.presence" defaultMessage="Presence" />
    </Menu.Header>
    <Menu
      as="dl"
      aria-label="Presence Menu"
      styleName="menu-nav"
      vertical
      inverted
      size="huge"
      onClick={onMenuSelect}
      role="navigation"
    >
      <Menu.Item as="dt" link styleName="menu-item-nav">
        <Link
          aria-label="RepVisits"
          aria-current={getRepVisitsActiveClass(router) ? 'true' : 'false'}
          to="/rep-visits/"
          id={`js-${idPrefix}-rep-visits-menu-link`}
          className={getRepVisitsActiveClass(router)}
          styleName="menu-link-nav-update"
          activeClassName={active}
        >
          <FormattedMessage tagName="h3" id="app.mainMenu.link.repvisits" defaultMessage="RepVisits" />
        </Link>
      </Menu.Item>
      <HasRole anyOf={[constants.ADMIN_ROLE, constants.PUBLISHING_ROLE]}>
        <SubscriptionActive anyOf={[constants.REP_VISITS_SUBSCRIPTION]}>
          <Menu.Item as="dt" link styleName="menu-item-nav">
            <Link
              aria-label="Events"
              aria-current={getCollegeEventsActiveClass(router) ? 'true' : 'false'}
              to="/college-events/"
              id={`js-${idPrefix}-college-events-menu-link`}
              className={getCollegeEventsActiveClass(router)}
              styleName="menu-link-nav-update"
              activeClassName={active}
            >
              <FormattedMessage tagName="h3" id="app.mainMenu.link.collegeEvents" defaultMessage="Events" />
            </Link>
          </Menu.Item>
        </SubscriptionActive>
      </HasRole>
    </Menu>
    <HasRole anyOf={constants.ADMIN_ROLE}>
      <SubscriptionActive anyOf={constants.CONNECTION_SUBSCRIPTION}>
        <Menu.Header tabIndex="0" as="div" styleName="menu-sub-header-nav">
          <FormattedMessage tagName="h5" id="app.mainMenu.subtitle.connections" defaultMessage="Connection" />
        </Menu.Header>
        <Menu
          as="dl"
          aria-label="Connection Menu"
          styleName="menu-nav"
          vertical
          inverted
          size="huge"
          onClick={onMenuSelect}
          role="navigation"
        >
          <Menu.Item as="dt" link styleName="menu-item-nav">
            <Link
              aria-label="Connection"
              aria-current={getConnectionClass(router) ? 'true' : 'false'}
              to="/connection"
              id={`js-${idPrefix}-connection-menu-link`}
              className={getConnectionClass(router)}
              styleName="menu-link-nav-update"
              activeClassName={active}
            >
              <div role="heading" aria-level="2">
                <FormattedMessage tagName="h3" id="app.mainMenu.link.connection" defaultMessage="Connection" />
              </div>
            </Link>
          </Menu.Item>
        </Menu>
      </SubscriptionActive>
      <SubscriptionInactive allOf={constants.CONNECTION_SUBSCRIPTION}>
        <SubscriptionActive anyOf={constants.AM_PLUS_SUBSCRIPTION}>
          <ActiveMatchConnectionsMenu
            idPrefix={idPrefix}
            router={router}
            onMenuSelect={onMenuSelect}
          />
        </SubscriptionActive>
      </SubscriptionInactive>
    </HasRole>
  </div>
);
ActiveMatchConnectionsMenu.propTypes = {
  router: PropTypes.shape({
    location: PropTypes.object.isRequired,
  }).isRequired,
  idPrefix: PropTypes.string.isRequired,
  onMenuSelect: PropTypes.func,
};
MainMenu.propTypes = {
  router: PropTypes.shape({
    location: PropTypes.object.isRequired,
  }).isRequired,
  enabledFeatures: PropTypes.array,
  communityProfile: PropTypes.object,
  idPrefix: PropTypes.string.isRequired,
  onMenuSelect: PropTypes.func,
};

export const MainMenuWithRouter = withRouter(MainMenu);
export default withCommunityProfileIdQuery(withEnabledFeatures(MainMenuWithRouter));
